/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext, useRef } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import IUser, {
  profileNames,
  userStatus,
  userAccount,
  badgeDriveLicence,
} from "../../../dataProvider/interfaces/IUser";
import {
  actionDialogType,
  LoadingButton,
  MediaImageUploader,
  SimpleActionDialog,
} from "../../../components";
import { APIContext } from "../../../store/APIStore";
import IDeveloperAccount from "../../../dataProvider/interfaces/IDeveloperAccount";
import { BiTrash, BiPencil } from "react-icons/bi";
import { theme } from "../../../theme";
import NotificationMessage, {
  notificationType,
} from "../../../components/NotificationMessage/NotificationMessage";
import { useHasPermissions } from "../../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../../roles";
import { StoreContext } from "../../../store/Store";
import FdTextField from "../../../components/UIComponents/TextField/FdTextfield";
import FdTypography from "../../../components/UIComponents/Typography/FdTypography";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import FdAutoComplete from "../../../components/UIComponents/AutoComplete/FdAutoComplete";
import FdButton from "../../../components/UIComponents/Button/FdButton";
import FdIconButton from "../../../components/UIComponents/IconButton/FdIconButton";
import { TextField } from "@mui/material";
import { userForm as classes } from "../styles";
import FdSelect from "../../../components/UIComponents/Select/FdSelect";

export interface Props {
  user?: IUser | null;
  onUserSave: Function;
  onError?: (error: string) => void;
}

interface FormData {
  account: {
    label: string;
    value: number;
  };
  username?: string;
  password?: string;
  email?: string;
  fullName?: string;
  profile?: profileNames;
}

const UserForm = ({ user, onUserSave, onError }: Props) => {
  const [userAccounts, setUserAccounts] = useState<userAccount[]>([]);
  const [attachedImage, setAttachedImage] = useState<File>();
  const [devInfo, setDevInfo] = useState<IDeveloperAccount | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    message: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
  });
  const [passwordFieldDisabled, setPasswordFieldDisabled] = useState(
    !!user?.id
  );
  const [state] = useContext(StoreContext);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const apiKeyButtonRef = useRef<any>();
  // let submit: Function;

  // permissions
  const canManageAccount = useHasPermissions(PERMISSIONS.CAN_MANAGE_ACCOUNT);
  const canManageDevAccount = useHasPermissions(
    PERMISSIONS.CAN_MANAGE_DEV_ACCOUNT
  );

  const getStatusList = () => {
    return (Object.keys(userStatus) as Array<keyof typeof userStatus>).map(
      (key) => {
        const value = userStatus[key];
        return (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        );
      }
    );
  };

  const getAccountList = () => {
    return userAccounts.map((account) => {
      return { label: account.name, value: account.id };
    });
  };

  const getLicenceList = () => {
    return (
      Object.keys(badgeDriveLicence) as Array<keyof typeof badgeDriveLicence>
    ).map((key) => {
      const value = badgeDriveLicence[key];
      const label =
        key === badgeDriveLicence.NONE
          ? "None"
          : key === badgeDriveLicence.LITE
            ? "Badgedrive Lite"
            : "Badgedrive Pro";
      return (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      );
    });
  };

  const getProfileList = () => {
    const loggedProfileIndex = (
      Object.values(profileNames) as string[]
    ).indexOf(state.currentUser?.profile.name);
    const userProfileIndex = user
      ? (Object.values(profileNames) as string[]).indexOf(user.profile.name)
      : -1;

    return (Object.keys(profileNames) as Array<keyof typeof profileNames>).map(
      (key, index) => {
        const value = profileNames[key];
        const label = value
          .split("_")
          .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
          .join(" ");
        return (
          <MenuItem
            key={value}
            value={value}
            disabled={
              index > loggedProfileIndex ||
              userProfileIndex > loggedProfileIndex
            } // we disable certain entries because a logged in user should not assign itself higher privileges and he should not change the profile for higher ranked users
          // for getting a profile rank level we are using the corresponding property index inside profileNames enum
          >
            {label}
          </MenuItem>
        );
      }
    );
  };

  const profileList = getProfileList();
  const statusList = getStatusList();
  const accountList = getAccountList();
  const licenceList = getLicenceList();

  // const initialAccount = accountList.find((el) => el.value === user?.account.id || state.currentUser?.account?.id || 1)
  const initialValues = {
    username: user?.username,
    password: user?.password,
    email: user?.email,
    fullName: user?.fullName || "",
    profile: user?.profile.name || profileNames.ONSITE_OPERATOR,
    status: user?.status || userStatus.ACTIVE,
    // account: initialAccount,
    account: user?.account.id || state.currentUser?.account?.id || 1,
    badgedrive_license:
      user?.badgedrive_license === null ||
        (user?.badgedrive_license as any) === "null"
        ? badgeDriveLicence.PRO
        : user?.badgedrive_license || badgeDriveLicence.PRO,
    // Dev Fields
    dev_apiKey: devInfo?.apiKey,
    dev_maxNbrOfCalls: devInfo?.maxNbrOfCallsPerMonth,
    dev_currentNbrOfCalls: devInfo?.numOfCallsThisMonth,
    dev_active: devInfo?.active,
  };

  const { register, handleSubmit, control, reset, formState: {errors} } = useForm({
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (canManageAccount) {
      fetchUserAccounts();
    }

    return () => {
      setUserAccounts([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageAccount]);

  useEffect(() => {
    // load dev information
    if (user && canManageDevAccount) {
      fetchDeveloperAccount();
    } else {
      setDevInfo(null);
    }
    setPasswordFieldDisabled(!!user?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canManageDevAccount]);


  useEffect(() => {
    reset(initialValues);
    if (user === null) {
      reset({
        username: '',
        password: '',
        email: '',
        fullName: '',
        profile: profileNames.ONSITE_OPERATOR,
        status: userStatus.ACTIVE,
        account: state.currentUser?.account?.id || 1,
        badgedrive_license: badgeDriveLicence.PRO,
        dev_apiKey: '',
        dev_maxNbrOfCalls: 0,
        dev_currentNbrOfCalls: 0,
        dev_active: false,
      })
    }
  }, [user, devInfo])



  // auto select the password field when trying to edit it
  useEffect(() => {
    // Only activate the pwd field in case of it being turned active and it's not a new user
    // (it's active by default in the latter case and we want the username field active, not the pwd)
    if (!passwordFieldDisabled && user?.id) {
      const passwordInput = document.getElementById(
        "password-input"
      ) as HTMLInputElement;

      passwordInput.focus();
      passwordInput.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordFieldDisabled]);

  const fetchUserAccounts = async () => {
    try {
      const response = await api.accountService.getAccounts();
      setUserAccounts(response.data);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchDeveloperAccount = async () => {
    try {
      if (user) {
        const accounts = await api.developerAccountService.getDeveloperAccount(
          user.id.toString()
        );
        // there should be only one dev account for every user
        if (accounts && accounts[0]) {
          setDevInfo(accounts[0]);
        } else {
          setDevInfo(null);
        }
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const showDeleteKeyWarning = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: true,
      message: "Are you sure you want to delete the dev api key?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      actionCallback: handleDeleteDevKey,
    });
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const handleDeleteDevKey = async () => {
    try {
      if (devInfo) {
        // show loading dialog
        setConfirmationDialog({
          ...confirmationDialog,
          open: true,
          message: "Deleting dev key...",
          isLoading: true,
        });

        // call the api
        await api.developerAccountService.deleteDeveloperAccount(
          devInfo.apiKey
        );

        // remove the loaded dev info
        setDevInfo(null);

        // close the dialog
        handleDialogClose();

        // show a notification
        notificationRef.current?.show(
          "Api key deleted",
          notificationType.Success
        );
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const generateApiKey = async () => {
    if (user) {
      apiKeyButtonRef.current.showLoading(true);
      const info = await api.developerAccountService.createDeveloperAccount(
        user.id.toString()
      );
      setDevInfo(info);
    }
  };

  const onSubmit = async (values: FormData) => {
    if (user) {
      updateUser(values);
    } else {
      createUser(values);
    }
  };

  const createUser = async (values: FormData) => {
    try {
      const newUser = (await api.userService.create({
        ...values,
        account: values?.account?.value
      })) as any;

      if (attachedImage) {
        const imageData = (await api.fileService.uploadImageFile("users", {
          resourceId: newUser.id,
          file: attachedImage,
        })) as any;
        const params = {
          account: `${newUser.account.id}`,
          badgedrive_license: newUser.badgedrive_license,
          email: newUser.email,
          fullName: newUser.fullName,
          id: newUser.id,
          image: imageData.image,
          password: newUser.password,
          profile: newUser.profile.name,
          status: newUser.status,
          username: newUser.username,
        };

        const response = await api.userService.update(params);
        onUserSave(response);
      } else {
        onUserSave(newUser);
      }
    } catch (error: any) {
      console.log(error.response);
      onError && onError(error.response.data.message);
    }
  };

  const updateUser = async (values: any) => {
    let userImage;
    try {
      if (attachedImage && user) {
        const imageData = (await api.fileService.uploadImageFile("users", {
          resourceId: user?.id,
          file: attachedImage,
        })) as any;

        userImage = imageData.image;
      }
      const response = await api.userService.update({
        ...values,
        id: user?.id,
        image: userImage,
        account: values?.account?.value
      });

      if (devInfo) {
        await api.developerAccountService.updateDeveloperAccount(
          devInfo.apiKey,
          {
            active: values.dev_active,
            maxNbrOfCallsPerMonth: values.dev_maxNbrOfCalls,
            numOfCallsThisMonth: values.dev_currentNbrOfCalls,
          }
        );
      }
      onUserSave(response);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // async function validate(values: FormData) {
  //   if (!values.username) {
  //     return { username: "Required" };
  //   }

  //   if (!values.password && !user?.id) {
  //     return { password: "Required" };
  //   }

  //   if (!values.email) {
  //     return { email: "Required" };
  //   }

  //   return;
  // }

  // const validate = {
  //   maxLength: (v: string | any[]) =>
  //     v.length <= 50 || "The email should have at most 50 characters",
  //   matchPattern: (v: string) =>
  //     /^[A-Za-z0-9+_.-]+@(.+)$/.test(v) ||
  //     "Email address must be a valid address",
  // }
  return (
    <>
      <Box
        sx={[classes.userFormWrapper, {border: !user ? `5px solid ${theme.palette.primary.main}` : undefined}]}
      >
        <Box sx={classes.infoTitle}>
          <Box sx={classes.userImage}>
            <MediaImageUploader
              onImageSelect={(file: File) => {
                setAttachedImage(file);
              }}
              imageUrl={user?.image}
            />
          </Box>

          <Box textAlign="left">
            <FdTypography
              label={user?.fullName || "New user"}
              variant="h2"
              component="h2"
              mb="10px"
            />
            <FdTypography label={user?.profile.name} />
            <FdTypography label={user?.status} />
          </Box>
        </Box>
        <Box sx={classes.infoFields}>
          <form
            id="userForm"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ width: "100%", textAlign: "left" }}
          >
            <Box sx={classes.formRoot}>
              {/* username */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="username"
                  label="Username"
                  InputProps={{
                    autoComplete: "off",
                    id: "username-input",
                  }}
                  required
                  error={errors.username?.type === "required"}
                  errorMsg={errors.username && "UserName is required"}
                />
              </Box>
              <Box display="flex" alignItems="center" marginBottom="15px">
                <FdTextField
                  register={register}
                  name="password"
                  label="Password"
                  type="password"
                  inputProps={{
                    autoComplete: "new-password",
                    id: "password-input",
                  }}
                  disabled={passwordFieldDisabled}
                />
                <FdIconButton
                  onClick={(e: any) => {
                    setPasswordFieldDisabled(false);
                  }}
                  sx={[classes.editPasswordButton, { color: theme.palette.primary.main,}]}
                  Icon={<BiPencil title="Edit password" size="15" />}
                />
              </Box>

              {/* email */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="email"
                  id="email"
                  label="Email"
                  placeholder=""
                  type="email"
                  required
                  //@ts-ignore
                  error={errors?.email?.type === "required" || errors?.email?.message}
                  errorMsg={errors.email ? errors?.email?.message || "Email is required" : ''}
                  // validationObject={validate}
                />
              </Box>

              {/* fullname */}
              <Box marginBottom="15px">
                <FdTextField
                  register={register}
                  name="fullName"
                  id="fullName"
                  label="Full Name"
                  placeholder=""
                />
              </Box>

              {/* user profile   */}
              <Box marginBottom="15px">
                <Controller
                  {...register("profile")}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FdSelect value={value} name="profile" onChange={onChange} label="Profile">
                      {profileList}
                    </FdSelect>
                  )}
                />
              </Box>
              {/* user status */}
              <Box marginBottom="15px">
                <Controller
                  {...register("status")}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FdSelect value={value} name="status" onChange={onChange} label="status">
                      {statusList}
                    </FdSelect>
                  )}
                />
              </Box>

              {/* account */}
              {canManageAccount && (
                <Box marginBottom="15px">
                  {accountList.length > 0 && (
                    <Controller
                      control={control}
                      name="account"
                      render={({ field: { onChange, value } }) => {
                        const temp = accountList.find(item => item.value === value);
                        return (
                          <>
                            <FdAutoComplete
                              options={accountList}
                              onChange={(event, values) => onChange(values)}
                              renderInput={(params) => {
                                return (
                                  <TextField
                                    {...params}
                                    label="Account"
                                    InputLabelProps={{
                                      shrink: true
                                    }}
                                    variant="outlined"
                                  />
                                );
                              }}
                              value={temp?.label || value}
                            />
                          </>
                        )}}
                    />
                  )}
                </Box>
              )}
              {/* badgedrive licence */}
              <Box marginBottom="15px">
                <Controller
                  {...register("badgedrive_license")}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FdSelect value={value} name="badgedrive_license" onChange={onChange} label="badgedrive_license">
                      {licenceList}
                    </FdSelect>
                  )}
                />
              </Box>
              {/* Dev account info */}
              {user && !devInfo && canManageDevAccount && (
                <LoadingButton
                  ref={apiKeyButtonRef}
                  label="Generate API Key"
                  variant="outlined"
                  onClick={() => {
                    generateApiKey();
                  }}
                />
              )}
              {devInfo && (
                <Box marginTop="25px">
                  <FdTypography
                    label="Dev account information"
                    component="h2"
                    fontWeight="bold"
                    marginBottom="15px"
                    color="black"
                  />
                  <Box display="flex" alignItems="center" marginBottom="15px">
                    <FdTextField
                      register={register}
                      name="dev_apiKey"
                      label="API Key"
                      disabled
                    />
                    <FdIconButton
                      aria-label="deleteBtn"
                      onClick={() => {
                        showDeleteKeyWarning();
                      }}
                      sx={[classes.editPasswordButton,
                      {color: theme.palette.error.main,}]
                      }
                      Icon={<BiTrash title="Delete key" size="15" />}
                    />

                  </Box>
                  <Box marginBottom="15px">
                    <FdTextField
                      register={register}
                      name="dev_maxNbrOfCalls"
                      label="Max number of calls per month"
                      type="number"
                    />
                  </Box>
                  <Box marginBottom="15px">
                    <FdTextField
                      register={register}
                      name="dev_currentNbrOfCalls"
                      label="Number of calls this month"
                      type="number"
                    />
                  </Box>
                  {/* <FdCheckbox
                    name="dev_active"
                    register={register}
                    label="Active"
                    value={false}
                    defaultValue="true"
                    // data={{ label: "Active", value: false }}
                    className={`${classes.checkbox}`}
                  /> */}
                </Box>
              )}
            </Box>
          </form>
        </Box>

        <FdButton
          sx={classes.saveButton}
          form="userForm"
          type="submit"
          ButtonName="Save"
        />
      </Box>

      <NotificationMessage ref={notificationRef} />

      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.message}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.actionCallback as Function}
          onCancel={handleDialogClose}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
};

export default UserForm;
